/**
 * Ханшийн графикийн интерактив давхарга — SVG дээрх HTML.
 *
 * ⚠️ Цэг, шошго, үнийн шошго ЯАГААД HTML вэ: SVG нь
 *    `preserveAspectRatio="none"` тул `<circle>` зууван, `<text>`
 *    сунгагдана. Байрлалыг `atPoint` / `atValue` хувиар өгнө —
 *    тэдгээр нь SVG-ийн PAD-ыг яг давтдаг тул цэг шугам дээрээ таарна.
 *
 * ⚠️ Үнийн тэнхлэг БАРУУН талд — trading-ийн зуршил.
 *
 * ⚠️ Цэг бүрт тоо бичихгүй. Зөвхөн оргил, ёроол, сүүлчийн утга шууд
 *    шошготой; бусад нь tooltip болон тэнхлэгээс уншигдана.
 */

import { formatDate, formatRate } from '@boots/domain';
import { FxPlot, fxGeometry, atPoint, atValue } from './fx-plot';

export interface FxRow {
  date: string;
  rate: string;
}

/** Тэнхлэгийн богино огноо: 90 хүртэл «08.24», цаашид «2026.08». */
export function axisDate(iso: string | undefined, days: number): string {
  const full = formatDate(iso);
  if (full === '—') return full;
  return days > 90 ? full.slice(0, 7) : full.slice(5);
}

/** Захын шошго тайрагдахгүй — эхнийх зүүн, сүүлчийнх баруун талдаа. */
function edgeShift(xPct: number): string {
  // Босго нь 0/100 БИШ: 320px дээр график ~204px тул ~65px шошго
  // 7%-д төвлөрвөл картаас 17px гардаг. Захын 10%-д тулгаж наана.
  if (xPct <= 10) return 'translateX(0)';
  if (xPct >= 90) return 'translateX(-100%)';
  return 'translateX(-50%)';
}

/** Стандарт хазайлт — мужийн хэлбэлзлийн хэмжүүр. */
function stdev(values: number[]): number {
  if (values.length < 2) return 0;
  const mean = values.reduce((a, b) => a + b, 0) / values.length;
  const variance = values.reduce((a, b) => a + (b - mean) ** 2, 0) / values.length;
  return Math.sqrt(variance);
}

/**
 * Өөрчлөлтийн заалт. Толгой ба tooltip хоёулаа хэрэглэнэ.
 *
 * ⚠️ Өнгө ГАНЦААРАА утга зөөхгүй — сум (▲/▼) ба тэмдэг (+/−) хоёр
 *    давхар кодолно. Оюу↔ягаан хосын deuteran ялгаа ΔE 5.4 буюу
 *    хангалтгүй тул өнгөнд найдвал улаан-ногоон сохор хүн уншихгүй.
 */
export function Move({ value, pct }: { value: number; pct: number | null }) {
  const up = value >= 0;
  return (
    <span className={`text-body whitespace-nowrap ${up ? 'text-alert-ink' : 'text-accent-700'}`}>
      <span aria-hidden className="mr-0.5">{up ? '▲' : '▼'}</span>
      {up ? '+' : '−'}
      {formatRate(Math.abs(value))}
      {pct === null ? null : ` (${up ? '+' : '−'}${Math.abs(pct).toFixed(2)}%)`}
    </span>
  );
}

export function FxChart({
  rows, values, days, hover, onHover, showValue, height = 190,
}: {
  rows: FxRow[];
  values: number[];
  days: number;
  hover: number | null;
  onHover: (i: number | null) => void;
  showValue: boolean;
  /** Зургийн өндөр, px. Дашбоардын нарийн баганад 96. */
  height?: number;
}) {
  // Лавлах = мужийн ЭХНИЙ ханш. «Муж эхлэхэд байсантай харьцуулбал?»
  const baseline = values[0]!;
  const g = fxGeometry(values, baseline);

  const lastIdx = values.length - 1;
  const hiIdx = values.indexOf(g.hi);
  const loIdx = values.indexOf(g.lo);
  const up = values[lastIdx]! >= baseline;

  // Тэнхлэгт 4 огноо — 365 цэг бүрт шошго тавихгүй
  const marks = [...new Set([0, Math.round(lastIdx / 3), Math.round((lastIdx * 2) / 3), lastIdx])];

  const hovered = hover === null ? null : rows[hover];
  const hoverPrev = hover !== null && hover > 0 ? (values[hover - 1] ?? null) : null;

  return (
    <>
      <div className="flex">
        {/* ── Зурмал талбай ────────────────────────────────── */}
        <div className="relative min-w-0 flex-1" style={{ height }}>
          <FxPlot
            values={values}
            geometry={g}
            hover={hover}
            onHover={onHover}
            label={
              `${values.length} өдрийн ханшийн график. ` +
              `Эхлэл ${formatRate(baseline)}₮, өнөөдөр ${formatRate(values[lastIdx])}₮, ` +
              `хамгийн өндөр ${formatRate(g.hi)}₮, хамгийн бага ${formatRate(g.lo)}₮.`
            }
          />

          {showValue ? (
            <>
              <Marker at={g.points[hiIdx]!} value={g.hi} date={rows[hiIdx]?.date} side="top" />
              <Marker at={g.points[loIdx]!} value={g.lo} date={rows[loIdx]?.date} side="bottom" />
            </>
          ) : null}

          {/* Сүүлийн цэг */}
          <span
            aria-hidden
            style={atPoint(g.points[lastIdx]!)}
            className={`absolute size-2.5 -translate-x-1/2 -translate-y-1/2 rounded-pill
              ring-2 ring-panel ${up ? 'bg-alert' : 'bg-accent'}`}
          />

          {/* Хулганы доорх цэг */}
          {hover !== null && g.points[hover] ? (
            <span
              aria-hidden
              style={atPoint(g.points[hover]!)}
              className="absolute size-2.5 -translate-x-1/2 -translate-y-1/2 rounded-pill
                bg-panel ring-2 ring-ink-2"
            />
          ) : null}

          {/* Tooltip — утга тод, огноо хоёрдогч (dataviz: values lead) */}
          {hovered ? (
            <div
              role="status"
              style={{
                left: `${g.points[hover!]!.xPct}%`,
                transform: g.points[hover!]!.xPct > 55 ? 'translateX(-104%)' : 'translateX(4%)',
              }}
              className="pointer-events-none absolute top-0 flex flex-col gap-0.5 rounded-well
                border border-hair bg-panel px-2.5 py-1.5 whitespace-nowrap shadow-pop"
            >
              <span className="num text-h3">{formatRate(hovered.rate)}₮</span>
              <span className="text-micro text-ink-2">{formatDate(hovered.date)}</span>
              {hoverPrev === null ? null : (
                <span className="text-micro">
                  <Move value={Number(hovered.rate) - hoverPrev} pct={null} />
                </span>
              )}
            </div>
          ) : null}
        </div>

        {/* ── Үнийн тэнхлэг ────────────────────────────────── */}
        <div className="relative w-13 shrink-0" aria-hidden>
          {g.ticks.map((t) => (
            <span
              key={t.value}
              style={{ top: atValue(t.yPct) }}
              className="num absolute right-0 -translate-y-1/2 text-micro text-ink-3"
            >
              {formatRate(t.value)}
            </span>
          ))}

          {/* Одоогийн ханшийн шошго — тэнхлэг дээр наалдана */}
          <span
            style={{ top: atValue(g.points[lastIdx]!.yPct) }}
            className={`num absolute right-0 -translate-y-1/2 rounded-pill px-1.5 py-px
              text-micro text-white ${up ? 'bg-alert' : 'bg-accent-fill'}`}
          >
            {formatRate(values[lastIdx])}
          </span>
        </div>
      </div>

      {/* ── X тэнхлэг — HTML, SVG биш (сунгагдахгүй, кирилл уншигдана) ── */}
      <div className="relative h-4 pr-13" aria-hidden>
        {marks.map((i) => (
          <span
            key={i}
            style={{ left: `${g.points[i]!.xPct}%`, transform: edgeShift(g.points[i]!.xPct) }}
            className="num absolute text-micro text-ink-3"
          >
            {i === lastIdx ? 'Өнөөдөр' : axisDate(rows[i]?.date, days)}
          </span>
        ))}
      </div>

      {showValue ? (
        <div className="flex flex-wrap gap-x-5 gap-y-1 border-t border-hair pt-2.5 text-label text-ink-2">
          <Stat label="Хамгийн өндөр" value={`${formatRate(g.hi)}₮`} note={axisDate(rows[hiIdx]?.date, days)} />
          <Stat label="Хамгийн бага" value={`${formatRate(g.lo)}₮`} note={axisDate(rows[loIdx]?.date, days)} />
          <Stat label="Хэлбэлзэл" value={`±${formatRate(stdev(values))}₮`} note="стандарт хазайлт" />
        </div>
      ) : null}
    </>
  );
}

/** Оргил, ёроолын шууд шошго. */
function Marker({
  at, value, date, side,
}: {
  at: { xPct: number; yPct: number };
  value: number;
  date: string | undefined;
  side: 'top' | 'bottom';
}) {
  return (
    <span
      aria-hidden
      style={{ ...atPoint(at), transform: edgeShift(at.xPct) }}
      className="absolute flex flex-col items-center"
    >
      <span
        className={`num rounded-pill bg-panel/85 px-1 text-micro text-ink-2 ${
          side === 'top' ? '-mt-4.5' : 'mt-2'
        }`}
      >
        {formatRate(value)}
        {date ? <span className="ml-1 text-ink-3">{axisDate(date, 0)}</span> : null}
      </span>
    </span>
  );
}

function Stat({ label, value, note }: { label: string; value: string; note: string }) {
  return (
    <span className="flex items-baseline gap-1.5 whitespace-nowrap">
      {label}
      <span className="num text-body text-ink">{value}</span>
      <span className="text-micro text-ink-3">{note}</span>
    </span>
  );
}
